import { useState } from 'react'
import { useCopybookStore, type FontDirection, type GridType, type StrokeType, type TraceType, type PaperSize, getEffectiveLayout } from '../store/copybookStore'
import { generatePDF } from './PdfExport'
import ZeoSevenFonts, { COPYBOOK_FONTS } from './ZeoSevenFonts'

interface SectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
  collapsed?: boolean
  onToggle?: (open: boolean) => void
}

function Section({ title, children, defaultOpen = true, collapsed, onToggle }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const isControlled = collapsed !== undefined
  const isOpen = isControlled ? !collapsed : open

  const handleToggle = () => {
    if (isControlled) {
      onToggle?.(!isOpen)
    } else {
      setOpen(!open)
    }
  }

  return (
    <div className="border-b border-gray-100">
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-800">{title}</span>
        <span className="rounded-full border border-gray-300 bg-white/70 px-3 py-1 text-xs font-medium text-gray-700 shadow-sm">
          {isOpen ? '收起' : '展開'}
        </span>
      </button>
      {isOpen && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  )
}

function Slider({ label, value, min, max, step = 1, onChange, unit = '' }: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  onChange: (v: number) => void
  unit?: string
}) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900 font-medium">{value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
      />
    </div>
  )
}

function Toggle({ label, checked, onChange }: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-gray-600">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </button>
    </label>
  )
}

export default function ControlPanel() {
  const store = useCopybookStore()
  const layout = getEffectiveLayout({
    paperSize: store.paperSize,
    marginTop: store.marginTop,
    marginBottom: store.marginBottom,
    marginLeft: store.marginLeft,
    marginRight: store.marginRight,
    gridSize: store.gridSize,
    colGap: store.colGap,
    rowGap: store.rowGap,
    colsPerRow: store.colsPerRow,
    printTitleAndSource: store.printTitleAndSource,
    title: store.title,
    subtitle: store.subtitle,
    autoFill: store.autoFill,
  })
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced'>('basic')
  const [isGenerating, setIsGenerating] = useState(false)
  const [paperSectionCollapsed, setPaperSectionCollapsed] = useState(true)

  const paperSizes: { value: PaperSize; label: string }[] = [
    { value: 'A4P', label: 'A4 直式' },
    { value: 'A4L', label: 'A4 橫式' },
    { value: 'A3P', label: 'A3 直式' },
    { value: 'A3L', label: 'A3 橫式' },
    { value: 'A5P', label: 'A5 直式' },
    { value: 'A5L', label: 'A5 橫式' },
    { value: 'B4P', label: 'B4 直式' },
    { value: 'B4L', label: 'B4 橫式' },
    { value: 'B5P', label: 'B5 直式' },
    { value: 'B5L', label: 'B5 橫式' },
  ]

  const gridTypes: { value: GridType; label: string }[] = [
    { value: 'tian', label: '田字格' },
    { value: 'mi', label: '米字格' },
    { value: 'huigong', label: '回宮格' },
    { value: 'jiugong', label: '九宮格' },
    { value: 'blank', label: '空白格' },
  ]
  const fontDirections: { value: FontDirection; label: string }[] = [
    { value: 'horizontal', label: '橫排' },
    { value: 'vertical', label: '直排' },
  ]

  return (
    <div className="pb-20">
      {/* Mode Toggle */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('basic')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'basic' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'
            }`}
          >
            新手模式
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'advanced' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'
            }`}
          >
            進階模式
          </button>
        </div>
      </div>

      {/* Paper Section */}
      <Section
        title="紙張與版式"
        collapsed={paperSectionCollapsed}
        onToggle={(open) => setPaperSectionCollapsed(!open)}
      >
        <div>
          <label className="text-sm text-gray-600 block mb-2">紙張尺寸</label>
          <div className="grid grid-cols-2 gap-2">
            {paperSizes.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => {
                  store.setPaperSize(s.value)
                  setTimeout(() => setPaperSectionCollapsed(true), 200)
                }}
                className={`py-2 px-3 rounded-lg text-sm border transition-colors ${
                  store.paperSize === s.value
                    ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium'
                    : 'border-gray-400 hover:border-gray-500 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <Slider label="上邊距" value={store.marginTop} min={5} max={30} onChange={store.setMarginTop} unit="mm" />
        <Slider label="下邊距" value={store.marginBottom} min={5} max={30} onChange={store.setMarginBottom} unit="mm" />
        <Slider label="左邊距" value={store.marginLeft} min={5} max={30} onChange={store.setMarginLeft} unit="mm" />
        <Slider label="右邊距" value={store.marginRight} min={5} max={30} onChange={store.setMarginRight} unit="mm" />
        {activeTab === 'advanced' && (
          <Toggle label="顯示頁碼" checked={store.showPageNumber} onChange={store.setShowPageNumber} />
        )}
      </Section>

      {/* Grid Section */}
      <Section title="格線系統">
        <Toggle label="自動滿版列印區" checked={store.autoFill} onChange={store.setAutoFill} />
        <div>
          <label className="text-sm text-gray-600 block mb-2">格線類型</label>
          <div className="grid grid-cols-2 gap-2">
            {gridTypes.map((g) => (
              <button
                key={g.value}
                onClick={() => store.setGridType(g.value)}
                className={`py-2 px-3 rounded-lg text-sm border transition-colors ${
                  store.gridType === g.value
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-400 hover:border-gray-500'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <Slider label="格子大小" value={store.gridSize} min={10} max={20} onChange={store.setGridSize} unit="mm" />
        <Slider label="格線濃度" value={store.gridOpacity} min={10} max={100} onChange={store.setGridOpacity} unit="%" />
        <Slider label="格線粗細" value={store.gridLineWidth} min={0.2} max={3} step={0.1} onChange={store.setGridLineWidth} unit="px" />
        <div>
          <div className="flex justify-between items-center text-sm mb-1.5">
            <span className="text-gray-600">格線顏色</span>
            <span className="text-gray-900 font-mono text-xs font-medium uppercase">{store.gridColor}</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={store.gridColor}
              onChange={(e) => store.setGridColor(e.target.value)}
              className="w-9 h-8 rounded-lg border border-gray-300 cursor-pointer p-0.5"
            />
            <div className="flex flex-wrap gap-1.5 flex-1">
              {[
                { label: '淺灰', value: '#9ca3af' },
                { label: '硃砂紅', value: '#dc2626' },
                { label: '墨綠', value: '#16a34a' },
                { label: '黛藍', value: '#2563eb' },
                { label: '琥珀', value: '#d97706' },
                { label: '深灰', value: '#4b5563' },
              ].map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => store.setGridColor(c.value)}
                  title={c.label}
                  className={`w-6 h-6 rounded-full border transition-transform flex items-center justify-center ${
                    store.gridColor.toLowerCase() === c.value.toLowerCase()
                      ? 'ring-2 ring-blue-500 ring-offset-1 scale-110 border-transparent shadow-sm'
                      : 'border-gray-300 hover:scale-105'
                  }`}
                  style={{ backgroundColor: c.value }}
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-600 block mb-2">字體方向</label>
          <div className="flex gap-2">
            {fontDirections.map((direction) => (
              <button
                key={direction.value}
                onClick={() => store.setFontDirection(direction.value)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm border transition-colors ${
                  store.fontDirection === direction.value
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-400 hover:border-gray-500'
                }`}
              >
                {direction.label}
              </button>
            ))}
          </div>
        </div>
        <Toggle
          label="保留文字換行（按行排版）"
          checked={store.preserveLineBreaks}
          onChange={store.setPreserveLineBreaks}
        />
        {activeTab === 'advanced' && (
          <>
            {store.autoFill ? (
              <div className="flex justify-between items-center text-sm py-2 bg-blue-50/50 px-3 rounded-lg border border-blue-100/50 my-2">
                <span className="text-gray-600 font-medium">每行格數 (自動計算)</span>
                <span className="text-blue-700 font-semibold">{layout.cols} 格</span>
              </div>
            ) : (
              <Slider label="每行格數" value={store.colsPerRow} min={4} max={20} onChange={store.setColsPerRow} />
            )}
            <Slider 
              label={store.autoFill ? "最小行距" : "行距"} 
              value={store.rowGap} 
              min={0} 
              max={20} 
              onChange={store.setRowGap} 
              unit="mm" 
            />
            <Slider 
              label={store.autoFill ? "最小列距" : "列距"} 
              value={store.colGap} 
              min={0} 
              max={20} 
              onChange={store.setColGap} 
              unit="mm" 
            />
            {store.autoFill && (
              <div className="text-xs text-gray-500 space-y-1.5 bg-gray-50/80 p-3 rounded-xl border border-gray-100 my-2 shadow-inner">
                <div className="flex justify-between">
                  <span className="text-gray-500">實際列距 (水平):</span>
                  <span className="font-mono text-gray-700 font-semibold">{layout.colGap.toFixed(2)} mm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">實際行距 (垂直):</span>
                  <span className="font-mono text-gray-700 font-semibold">{layout.rowGap.toFixed(2)} mm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">每頁行數 (垂直):</span>
                  <span className="font-mono text-gray-700 font-semibold">{layout.rows} 行</span>
                </div>
              </div>
            )}
            <Slider label="圓角" value={store.gridRadius} min={0} max={8} onChange={store.setGridRadius} unit="px" />
          </>
        )}
      </Section>

      {/* Stroke Section */}
      <Section title="筆順與描紅" defaultOpen={false}>
        <Toggle label="顯示筆順" checked={store.showStroke} onChange={store.setShowStroke} />
        {store.showStroke && (
          <>
            <div>
              <label className="text-sm text-gray-600 block mb-2">筆順類型</label>
              <div className="flex gap-2">
                {(['color', 'number', 'arrow'] as StrokeType[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => store.setStrokeType(t)}
                    className={`flex-1 py-1.5 px-2 rounded-lg text-xs border transition-colors ${
                      store.strokeType === t
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-400'
                    }`}
                  >
                    {t === 'color' ? '彩色分步' : t === 'number' ? '數字序號' : '傳統箭頭'}
                  </button>
                ))}
              </div>
            </div>
            <Slider label="重複次數" value={store.strokeRepeat} min={1} max={3} onChange={store.setStrokeRepeat} />
            <Toggle label="末筆高亮" checked={store.highlightLastStroke} onChange={store.setHighlightLastStroke} />
          </>
        )}
        <Toggle label="描紅模式" checked={store.showTrace} onChange={store.setShowTrace} />
        {store.showTrace && (
          <div className="bg-blue-50/50 border border-blue-100/60 rounded-xl p-3 mt-2 space-y-3">
            <div>
              <label className="text-sm text-gray-600 block mb-1">描紅類型</label>
              <div className="flex bg-gray-200/50 rounded-lg p-0.5">
                {(['solid', 'hollow'] as TraceType[]).map((type) => {
                  const active = store.traceInterval ? false : (store.traceType === type)
                  return (
                    <button
                      key={type}
                      disabled={store.traceInterval}
                      onClick={() => store.setTraceType(type)}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                        store.traceInterval 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : active
                            ? 'bg-blue-100 text-blue-700 shadow-sm font-semibold'
                            : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      {type === 'solid' ? '實心描紅' : '空心描紅'}
                    </button>
                  )
                })}
              </div>
              {store.traceInterval && (
                <p className="text-[11px] text-gray-500 mt-1">※ 已啟用間隔描字，將自動以一實一空交替呈現。</p>
              )}
            </div>
            <Slider label="空心線寬度" value={store.traceLineWidth} min={0.3} max={3} step={0.1} onChange={store.setTraceLineWidth} unit="px" />
            <Toggle label="間隔描字（描一空一）" checked={store.traceInterval} onChange={store.setTraceInterval} />
          </div>
        )}
        <Toggle
          label={`臨帖模式（一字${store.copyBlankCount > 1 ? `空 ${store.copyBlankCount} 格` : '一空'}）`}
          checked={store.copyInterval}
          onChange={store.setCopyInterval}
        />
        {store.copyInterval && (
          <Slider
            label="每字後留白"
            value={store.copyBlankCount}
            min={1}
            max={6}
            step={1}
            onChange={store.setCopyBlankCount}
            unit=" 格"
          />
        )}
        <Toggle
          label={`行間隔臨帖（一行字${store.copyRowBlankCount > 1 ? `空 ${store.copyRowBlankCount} 行` : '空 1 行'}）`}
          checked={store.copyRowInterval}
          onChange={store.setCopyRowInterval}
        />
        {store.copyRowInterval && (
          <Slider
            label="每行後留白行數"
            value={store.copyRowBlankCount}
            min={1}
            max={6}
            step={1}
            onChange={store.setCopyRowBlankCount}
            unit=" 行"
          />
        )}
      </Section>



      {/* Font Selection */}
      <Section title="字型與文字顏色" defaultOpen={false}>
        <div className="flex gap-3 mb-4">
          <div className="flex-1">
            <label className="text-sm text-gray-600 block mb-1">字體顏色</label>
            <input
              type="color"
              value={store.exampleColor}
              onChange={(e) => store.setExampleColor(e.target.value)}
              className="w-full h-8 rounded cursor-pointer"
            />
          </div>
          {store.showTrace && (
            <div className="flex-1">
              <label className="text-sm text-gray-600 block mb-1">描字顏色</label>
              <input
                type="color"
                value={store.traceColor}
                onChange={(e) => store.setTraceColor(e.target.value)}
                className="w-full h-8 rounded cursor-pointer"
              />
            </div>
          )}
        </div>
        <ZeoSevenFonts
          selectedId={(() => {
            const getPrimary = (str: string) => str.replaceAll('"', '').split(',')[0].trim()
            const primary = getPrimary(store.fontFamily)
            const stdFont = COPYBOOK_FONTS.find(f => getPrimary(f.fontFamily) === primary)
            if (stdFont) return stdFont.id
            const locFont = store.localFonts.find(f => getPrimary(f.fontFamily) === primary)
            return locFont ? locFont.id : undefined
          })()}
          onSelect={(font) => {
            store.setFontFamily(font.fontFamily)
          }}
        />
      </Section>

      {/* Page Info */}
      <Section title="頁首頁尾" defaultOpen={false}>
        <Toggle
          label="列印標題與來源"
          checked={store.printTitleAndSource}
          onChange={store.setPrintTitleAndSource}
        />
        <div>
          <label className="text-sm text-gray-600 block mb-1">頁首標題</label>
          <input
            type="text"
            value={store.title}
            onChange={(e) => store.setTitle(e.target.value)}
            placeholder="字帖練習"
            className="w-full p-2 border-2 border-gray-400 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="text-sm text-gray-600 block mb-1">頁首內容</label>
          <input
            type="text"
            value={store.subtitle}
            onChange={(e) => store.setSubtitle(e.target.value)}
              className="w-full p-2 border-2 border-gray-400 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="text-sm text-gray-600 block mb-1">頁尾內容</label>
          <input
            type="text"
            value={store.footer}
            onChange={(e) => store.setFooter(e.target.value)}
              className="w-full p-2 border-2 border-gray-400 rounded-lg text-sm"
          />
        </div>
      </Section>

      {/* Action Buttons */}
      <div className="p-4 space-y-2">
        <button
          disabled={isGenerating}
          onClick={async () => {
            setIsGenerating(true)
            try {
              await generatePDF()
            } catch (err) {
              console.error(err)
              alert('產生 PDF 失敗，請檢查網路連線後重試')
            } finally {
              setIsGenerating(false)
            }
          }}
          className={`w-full py-3 text-white rounded-lg font-medium transition-colors ${
            isGenerating ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isGenerating ? '正在產生 PDF (下載字型中)...' : '產生 PDF'}
        </button>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => window.print()}
            className="w-full sm:flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
          >
            列印
          </button>
          <button
            onClick={() => {
              const state = useCopybookStore.getState()
              const blob = new Blob([JSON.stringify({
                text: state.text,
                title: state.title,
                subtitle: state.subtitle,
                footer: state.footer,
                printTitleAndSource: state.printTitleAndSource,
                paperSize: state.paperSize,
                marginTop: state.marginTop,
                marginBottom: state.marginBottom,
                marginLeft: state.marginLeft,
                marginRight: state.marginRight,
                showPageNumber: state.showPageNumber,
                gridType: state.gridType,
                gridSize: state.gridSize,
                colsPerRow: state.colsPerRow,
                rowGap: state.rowGap,
                colGap: state.colGap,
                gridOpacity: state.gridOpacity,
                gridRadius: state.gridRadius,
                gridLineWidth: state.gridLineWidth,
                gridColor: state.gridColor,
                showStroke: state.showStroke,
                strokeType: state.strokeType,
                strokeRepeat: state.strokeRepeat,
                highlightLastStroke: state.highlightLastStroke,
                showTrace: state.showTrace,
                traceType: state.traceType,
                traceColor: state.traceColor,
                exampleColor: state.exampleColor,
                traceLineWidth: state.traceLineWidth,
                traceInterval: state.traceInterval,
                copyInterval: state.copyInterval,
                copyBlankCount: state.copyBlankCount,
                copyRowInterval: state.copyRowInterval,
                copyRowBlankCount: state.copyRowBlankCount,
                showPhonetic: state.showPhonetic,
                phoneticBlank: state.phoneticBlank,
                showCorrection: state.showCorrection,
                fillLastPage: state.fillLastPage,
                autoFill: state.autoFill,
                fontFamily: state.fontFamily,
                fontDirection: state.fontDirection,
                advancedMode: state.advancedMode,
              }, null, 2)], { type: 'application/json' })
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = `字帖範本_${state.title || '練習'}.json`
              link.click()
              URL.revokeObjectURL(url)
            }}
            className="w-full sm:flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
          >
            儲存範本
          </button>
          <button
            onClick={() => {
              const input = document.createElement('input')
              input.type = 'file'
              input.accept = '.json'
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0]
                if (!file) return
                const reader = new FileReader()
                reader.onload = (event) => {
                  try {
                    const data = JSON.parse(event.target?.result as string)
                    
                    // 驗證並套用設定
                    if (data.text !== undefined) store.setText(data.text)
                    if (data.title !== undefined) store.setTitle(data.title)
                    if (data.subtitle !== undefined) store.setSubtitle(data.subtitle)
                    if (data.footer !== undefined) store.setFooter(data.footer)
                    if (data.printTitleAndSource !== undefined) store.setPrintTitleAndSource(data.printTitleAndSource)
                    if (data.paperSize !== undefined) store.setPaperSize(data.paperSize)
                    if (data.marginTop !== undefined) store.setMarginTop(data.marginTop)
                    if (data.marginBottom !== undefined) store.setMarginBottom(data.marginBottom)
                    if (data.marginLeft !== undefined) store.setMarginLeft(data.marginLeft)
                    if (data.marginRight !== undefined) store.setMarginRight(data.marginRight)
                    if (data.showPageNumber !== undefined) store.setShowPageNumber(data.showPageNumber)
                    if (data.gridType !== undefined) store.setGridType(data.gridType)
                    if (data.gridSize !== undefined) store.setGridSize(data.gridSize)
                    if (data.colsPerRow !== undefined) store.setColsPerRow(data.colsPerRow)
                    if (data.rowGap !== undefined) store.setRowGap(data.rowGap)
                    if (data.colGap !== undefined) store.setColGap(data.colGap)
                    if (data.gridOpacity !== undefined) store.setGridOpacity(data.gridOpacity)
                    if (data.gridRadius !== undefined) store.setGridRadius(data.gridRadius)
                    if (data.gridLineWidth !== undefined) store.setGridLineWidth(data.gridLineWidth)
                    if (data.gridColor !== undefined) store.setGridColor(data.gridColor)
                    if (data.showStroke !== undefined) store.setShowStroke(data.showStroke)
                    if (data.strokeType !== undefined) store.setStrokeType(data.strokeType)
                    if (data.strokeRepeat !== undefined) store.setStrokeRepeat(data.strokeRepeat)
                    if (data.highlightLastStroke !== undefined) store.setHighlightLastStroke(data.highlightLastStroke)
                    if (data.showTrace !== undefined) store.setShowTrace(data.showTrace)
                    if (data.traceType !== undefined) store.setTraceType(data.traceType)
                    if (data.traceColor !== undefined) store.setTraceColor(data.traceColor)
                    if (data.exampleColor !== undefined) store.setExampleColor(data.exampleColor)
                    if (data.traceLineWidth !== undefined) store.setTraceLineWidth(data.traceLineWidth)
                    if (data.traceInterval !== undefined) store.setTraceInterval(data.traceInterval)
                    if (data.copyInterval !== undefined) store.setCopyInterval(data.copyInterval)
                    if (data.copyBlankCount !== undefined) store.setCopyBlankCount(data.copyBlankCount)
                    if (data.copyRowInterval !== undefined) store.setCopyRowInterval(data.copyRowInterval)
                    if (data.copyRowBlankCount !== undefined) store.setCopyRowBlankCount(data.copyRowBlankCount)
                    if (data.showPhonetic !== undefined) store.setShowPhonetic(data.showPhonetic)
                    if (data.phoneticBlank !== undefined) store.setPhoneticBlank(data.phoneticBlank)
                    if (data.showCorrection !== undefined) store.setShowCorrection(data.showCorrection)
                    if (data.fillLastPage !== undefined) store.setFillLastPage(data.fillLastPage)
                    if (data.autoFill !== undefined) store.setAutoFill(data.autoFill)
                    if (data.fontFamily !== undefined) store.setFontFamily(data.fontFamily)
                    if (data.fontDirection !== undefined) store.setFontDirection(data.fontDirection)
                    if (data.advancedMode !== undefined) store.setAdvancedMode(data.advancedMode)
                    
                    alert('載入範本成功！')
                  } catch (err) {
                    console.error(err)
                    alert('載入範本失敗，請確認該檔案為正確的字帖範本 JSON 格式。')
                  }
                }
                reader.readAsText(file)
              }
              input.click()
            }}
            className="w-full sm:flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
          >
            載入範本
          </button>
        </div>
      </div>
    </div>
  )
}
